// src/hooks/items/useItemEffects.js
import { useRef, useEffect } from 'react';
import { isEVItem, applyEVItem } from '../../utils/evItemUtils';
import { getLearnsetTmMoves, getInheritedEggMoves, getPokemonLearnset } from '../../utils/pokemonLearnsets';
import { isRareCandyItem, resolveItemData, isEvolutionTriggerItem } from '../../utils/itemUsageRules';
import { isSoyYYNItem } from '../../utils/specialItemUtils';
import { findPokemonTemplate } from '../../utils/pokemonBaseStats';
import { getPokemonAbilities } from '../../utils/abilityUtils';
import { MAX_BERRY_PLANTER_SLOTS } from '../game/useBerryFarm';
import { feedNunmegiRace, buildNunmegiFeedMessage } from '../../utils/nunmegiRace';

// 아이템 nameEn → 변경 가능한 포켓몬 originalNumber 목록
const FORM_CHANGE_ITEMS = {
  'rotom-catalog': [479],
  'gracidea': [492],
  'meteorite': [386],
  'meteorite--2': [386],
  'meteorite--3': [386],
  'meteorite--4': [386],
  'furfrou-trim-ticket': [676],
};

// 로토무 카탈로그 등은 여러 번 재사용 가능한 카탈로그라 소모되지 않는다.
// 트리미앙 컷트 이용권은 "이용권"이라 1회 사용 후 소모되어야 한다.
const CONSUMABLE_FORM_CHANGE_ITEMS = new Set(['furfrou-trim-ticket']);

// category가 "evolution"이 아니어도 진화 체크를 같이 태워야 하는 예외 아이템.
// "고래왕급 고추장 떡볶이"(spicy_wailord, 관리자가 vitamins 카테고리의 친밀도 아이템으로
// 만들어서 진화 아이템 분기를 안 타던 커스텀 아이템)만 예외로 처리한다.
// 부적금화(amulet-coin)처럼 evolutions.json에 진화 아이템으로 등록된 아이템은
// isEvolutionTriggerItem이 진화 데이터 기준으로 따로 잡아준다.
const EVOLUTION_CHECK_EXCEPTION_ITEMS = new Set(['spicy_wailord']);

// 꿀 아이템 → 오리코리오 특정 폼 nameEn 직접 매핑
const NECTAR_FORM_MAP = {
  'red-nectar': 'oricorio-baile',
  'yellow-nectar': 'oricorio-pom-pom',
  'pink-nectar': 'oricorio-pau',
  'purple-nectar': 'oricorio-sensu',
};

export const useItemEffects = (
  currentUser,
  updateCurrentUser,
  updateOwnedPokemonByUniqueId,
  updateInventory,
  allItems,
  allMoves,
  pokemonLearnsets,
  useMoves,
  useEvolution,
  handleRareCandyWithEvolution,
  getPokemonFormCandidates,
  changePokemonForm,
  systemSettings = {},
  onRequestStatSelection = null,
  onRequestMoveChoice = null,
  onRequestQnaWrite = null,
  allPokemonMaster = [],
  onRequestAbilitySelect = null,
  adjustNumericField = null
) => {

  const movesHook = useMoves;
  const evolutionHook = useEvolution;
  const itemUseLockRef = useRef(null);
  const systemSettingsRef = useRef(systemSettings);
  useEffect(() => { systemSettingsRef.current = systemSettings; }, [systemSettings]);

  const useItemOnPokemon = async (item, pokemon, selectedFormNameEn = null, quantity = 1) => {
    if (!currentUser || !item) return;

    // 폼체인지/기술습득/특성패치처럼 대상(폼·기술·특성)을 매번 골라야 하는 분기는 이 값을
    // 참조하지 않으므로 항상 1개씩만 처리된다 — ItemsView 쪽에서도 그런 아이템에는 애초에
    // quantity 선택 UI를 보여주지 않는다(supportsBatchUse).
    const requestedQuantity = Math.max(1, Math.floor(Number(quantity) || 1));

    const itemKey = item?.itemId || item?.id || item?.name || 'unknown-item';
    const pokemonKey = pokemon?.uniqueId || pokemon?.id || pokemon?.name || 'unknown-pokemon';
    const useKey = `${itemKey}:${pokemonKey}`;

    if (itemUseLockRef.current === useKey) return;
    itemUseLockRef.current = useKey;

    const releaseItemUseLock = () => {
      window.setTimeout(() => {
        if (itemUseLockRef.current === useKey) {
          itemUseLockRef.current = null;
        }
      }, 750);
    };

    try {

    const itemData = resolveItemData(allItems, item);
    console.log('[useItemEffects] 아이템 사용 디버그:', {
      item_name: item.name,
      item_itemId: item.itemId,
      item_conditionBoost: item.conditionBoost,
      itemData_id: itemData?.id,
      itemData_name: itemData?.name,
      itemData_conditionBoost: itemData?.conditionBoost,
      src_will_be: itemData ? 'itemData' : 'item',
    });

    // ⭐ 인벤토리는 클로저 스냅샷이 아니라 Firebase의 최신 값을 기준으로 트랜잭션으로 소모한다.
    // (아이템을 연달아 여러 번 쓰거나 여러 포켓몬에게 나눠 쓸 때, 뒤 호출이 스테일한 인벤토리
    //  기준으로 통째로 덮어써서 앞선 소모분이 되살아나는 문제를 막기 위함)
    // extraUpdates(예: trainerExp)는 인벤토리와 무관한 필드라 별도 updateCurrentUser로 처리한다.
    const consumeItem = async (item, extraUpdates = {}, count = 1) => {
      if (currentUser.isSuperAdmin) {
        if (Object.keys(extraUpdates).length > 0) updateCurrentUser(extraUpdates);
        return;
      }

      const targetItemData = resolveItemData(allItems, item);
      const matchesItem = (inventoryItem) => {
        const inventoryItemData = resolveItemData(allItems, inventoryItem);

        if (targetItemData?.id != null && inventoryItemData?.id != null) {
          return targetItemData.id === inventoryItemData.id;
        }

        if (item.itemId != null && inventoryItem.itemId != null) {
          return item.itemId === inventoryItem.itemId;
        }

        return (
          (item.name && inventoryItem.name === item.name) ||
          (item.nameEn && inventoryItem.nameEn === item.nameEn)
        );
      };

      await updateInventory((currentInventory = []) => {
        let remaining = Math.max(1, Math.floor(Number(count) || 1));
        return currentInventory
          .map(i => {
            if (remaining > 0 && matchesItem(i)) {
              const take = Math.min(remaining, i.count);
              remaining -= take;
              return { ...i, count: i.count - take };
            }
            return i;
          })
          .filter(i => i.count > 0);
      });

      if (Object.keys(extraUpdates).length > 0) {
        updateCurrentUser(extraUpdates);
      }
    };

    // ⭐ 클로저 스냅샷이 아니라 Firebase의 최신 데이터를 기준으로 트랜잭션으로 해당 포켓몬만 갱신한다.
    // (같은 포켓몬/다른 포켓몬에게 아이템을 빠르게 연달아 쓸 때 앞선 변경이 사라지는 문제를 막기 위함)
    const updatePokemonInUser = async (updatedPokemon) => {
      await updateOwnedPokemonByUniqueId(updatedPokemon.uniqueId, () => updatedPokemon);
    };

    // itemData(마스터)가 있으면 마스터 데이터만, 없을 때만 item 데이터 사용
    // itemData(allItems 원본)에 호출자가 넘긴 item의 override(conditionBoost/evBoost/specialEffect 등)를 병합
    const src = itemData ? { ...itemData, ...Object.fromEntries(
      ['conditionBoost', 'evBoost', 'ivBoost', 'specialEffect', 'boostAmount', 'friendshipBoost', 'effortOverride']
        .filter(k => item[k] !== undefined)
        .map(k => [k, item[k]])
    ) } : item;
    if (src.isCustom && src.specialEffect === 'iv') {
      src.specialEffect = null;
    }

    // 멤버(트레이너) 본인 경험치 상승 — 대상 포켓몬 없이도 바로 사용 가능
    if (src.specialEffect === 'trainerExp') {
      const boost = Math.max(0, Number(src.boostAmount) || 0);
      if (boost <= 0) {
        alert('이 아이템은 상승량이 설정되어 있지 않습니다!');
        return;
      }
      const totalBoost = boost * requestedQuantity;
      // trainerExp도 다른 트레이너 대상 누적값과 동일하게 클로저 스냅샷이 아니라
      // runTransaction 기반 adjustNumericField로 처리한다 (CLAUDE.md 규칙).
      if (typeof adjustNumericField !== 'function') {
        alert('현재 이 아이템을 사용할 수 없습니다.');
        return;
      }
      await consumeItem(item, {}, requestedQuantity);
      await adjustNumericField('trainerExp', totalBoost);
      alert(`${item.name}을(를) ${requestedQuantity}개 사용해서 경험치를 ${totalBoost} 얻었습니다!`);
      return;
    }

    // 최대 포켓몬 슬롯 +N — 대상 포켓몬 없이도 바로 사용 가능, 사용한 멤버 개인에게만 누적 적용.
    // bonusPokemonSlots는 같은 아이템을 연달아 사용할 수 있는 누적값이라 trainerExp처럼
    // consumeItem의 closure 기반 extraUpdates가 아니라 runTransaction 기반 adjustNumericField로
    // 처리한다 (CLAUDE.md 재화/누적값 갱신 규칙 참고).
    if (src.specialEffect === 'maxPokemonSlots') {
      const boost = Math.max(0, Number(src.boostAmount) || 0);
      if (boost <= 0) {
        alert('이 아이템은 슬롯 증가량이 설정되어 있지 않습니다!');
        return;
      }
      if (typeof adjustNumericField !== 'function') {
        alert('현재 이 아이템을 사용할 수 없습니다.');
        return;
      }
      const totalBoost = boost * requestedQuantity;
      await consumeItem(item, {}, requestedQuantity);
      await adjustNumericField('bonusPokemonSlots', totalBoost);
      alert(`${item.name}을(를) ${requestedQuantity}개 사용해서 최대 포켓몬 슬롯이 +${totalBoost} 되었습니다!`);
      return;
    }

    // 나무열매플랜터 슬롯 +1 — maxPokemonSlots와 동일한 패턴, 최대 개수(4개)는 여기서도
    // 방어적으로 한 번 더 막는다(구매 제한은 상점에서 이미 걸지만 이중 안전장치).
    // 여러 개를 한 번에 쓰더라도 실제로 채울 수 있는 자리만큼만 소모한다.
    if (src.specialEffect === 'unlockBerryPlanter') {
      const currentSlots = Number(currentUser.berryPlanterSlots) || 0;
      const room = MAX_BERRY_PLANTER_SLOTS - currentSlots;
      if (room <= 0) {
        alert(`나무열매플랜터는 최대 ${MAX_BERRY_PLANTER_SLOTS}개까지만 가질 수 있습니다!`);
        return;
      }
      if (typeof adjustNumericField !== 'function') {
        alert('현재 이 아이템을 사용할 수 없습니다.');
        return;
      }
      const actualUse = Math.min(requestedQuantity, room);
      await consumeItem(item, {}, actualUse);
      await adjustNumericField('berryPlanterSlots', actualUse);
      alert(`${item.name}을(를) ${actualUse}개 사용해서 나무열매플랜터 슬롯이 +${actualUse} 되었습니다!`);
      return;
    }

    // 볼 변경 티켓 / 미용실 이용권 - 대상 포켓몬 없이 사용, 즉시 QnA "아이템" 탭 작성 모달을 띄운다.
    // 여기서는 소모하지 않음 (실제 글이 등록에 성공해야만 소모되도록 App.jsx의 QnA 작성 흐름에서 소모 처리)
    if (src.specialEffect === 'qnaItemPermit') {
      if (typeof onRequestQnaWrite !== 'function') {
        alert('현재 이 아이템을 사용할 수 없습니다.');
        return;
      }
      onRequestQnaWrite(item, src.permitKind || 'general');
      return;
    }

    // 누니머기의 눈덩이 - 대상 포켓몬 없이 가방에서 바로 사용. 모험 전리품으로 얻어 가방에
    // 쌓인 눈덩이를 여기서 소비하면, 상점 구매(useShop.feedNunmegiRace)와 똑같이 누니머기
    // 레이스를 사용한 개수만큼 전진시키고 "N번 누니머기가 눈덩이를 X개 먹었다!" 팝업을 띄운다.
    // 레이스 갱신은 utils/nunmegiRace.js에서 runTransaction으로 처리한다(CLAUDE.md 재화/누적값 규칙).
    if (src.specialEffect === 'nunmegiRace' || src.nunmegiRace || itemData?.nunmegiRace) {
      // 실제로 몇 개까지 먹일 수 있는지: 슈퍼 관리자는 consumeItem이 인벤토리를 소모하지
      // 않으므로(무한 사용) 요청 개수를 그대로, 그 외에는 최신 보유 수량으로 클램프한다.
      const matchesSnowball = (inv) => {
        const invData = resolveItemData(allItems, inv);
        if (itemData?.id != null && invData?.id != null) return itemData.id === invData.id;
        if (item.itemId != null && inv.itemId != null) return item.itemId === inv.itemId;
        return (item.name && inv.name === item.name) || (item.nameEn && inv.nameEn === item.nameEn);
      };
      const ownedCount = currentUser.isSuperAdmin
        ? requestedQuantity
        : (currentUser.inventory || []).reduce(
            (sum, inv) => (matchesSnowball(inv) ? sum + (Number(inv.count) || 0) : sum),
            0
          );
      const feedCount = Math.max(0, Math.min(requestedQuantity, ownedCount));
      if (feedCount <= 0) {
        alert('사용할 누니머기의 눈덩이가 없습니다!');
        return;
      }

      const fedRacers = await feedNunmegiRace(feedCount);
      if (fedRacers.length === 0) {
        alert('누니머기 레이스 반영 중 오류가 발생했습니다. 다시 시도해주세요.');
        return;
      }

      await consumeItem(item, {}, fedRacers.length);
      alert(buildNunmegiFeedMessage(fedRacers));
      return;
    }

    if (!pokemon) return;

    // 특성패치 - 이 포켓몬이 가질 수 있는 특성(숨겨진 특성 포함) 중 원하는 것을 골라 변경
    if (src.specialEffect === 'abilityPatch' && !item.__chosenAbility) {
      const template = findPokemonTemplate(pokemon, allPokemonMaster);
      const { abilities, hiddenAbility } = getPokemonAbilities(template);
      const options = [...abilities, ...(hiddenAbility ? [hiddenAbility] : [])]
        .filter((ability, index, all) => all.findIndex(a => a.nameEn === ability.nameEn) === index);

      if (options.length === 0) {
        alert('이 포켓몬의 특성 정보를 찾을 수 없습니다!');
        return;
      }
      if (typeof onRequestAbilitySelect !== 'function') {
        alert('현재 이 아이템을 사용할 수 없습니다.');
        return;
      }
      releaseItemUseLock();
      onRequestAbilitySelect(item, pokemon, options);
      return;
    }

    // 기술머신(범용)/하트비늘 - 배울 수 있는 기술 목록에서 하나를 골라 학습
    const isGenericTmItem = src.specialEffect === 'learnAnyTmMove';
    const isGenericEggItem = src.specialEffect === 'learnAnyEggMove';

    if (isGenericTmItem || isGenericEggItem) {
      // 2단계: 모달에서 이미 기술을 고른 뒤 재호출된 경우 - 바로 학습 + 소모
      if (item.__chosenMoveId) {
        const moveData = allMoves.find(m => m.id === item.__chosenMoveId);
        if (!moveData) {
          alert('기술 정보를 찾을 수 없습니다!');
          return;
        }
        const success = await movesHook.learnMove(pokemon.uniqueId, moveData, item.__chosenOldMoveId || null);
        if (success) {
          consumeItem(item);
        }
        return;
      }

      // 1단계: 배울 수 있는 기술 후보를 모아 선택 모달을 요청
      const learnset = getPokemonLearnset(pokemonLearnsets, pokemon);
      if (!learnset) {
        alert(`${pokemon.nickname || pokemon.name}의 기술 습득 정보를 찾을 수 없습니다!`);
        return;
      }

      const poolIds = isGenericTmItem ? getLearnsetTmMoves(learnset) : getInheritedEggMoves(pokemonLearnsets, pokemon);
      const knownMoveIds = new Set((pokemon.moves || []).map(m => m.moveId));
      const options = poolIds
        .filter(moveId => !knownMoveIds.has(moveId))
        .map(moveId => allMoves.find(m => m.id === moveId))
        .filter(Boolean);

      if (options.length === 0) {
        alert(`${pokemon.nickname || pokemon.name}이(가) 이 아이템으로 새로 배울 수 있는 기술이 없습니다!`);
        return;
      }

      if (typeof onRequestMoveChoice !== 'function') {
        alert('현재 이 아이템을 사용할 수 없습니다.');
        return;
      }

      releaseItemUseLock();
      onRequestMoveChoice(item, pokemon, options, isGenericTmItem ? 'tm' : 'egg');
      return;
    }

    // 기술머신 (TM) 사용 처리
    if (itemData?.isTM) {
      console.log('기술머신 사용:', itemData);

      let moveData = allMoves.find(m => m.id === itemData.moveId);

      if (!moveData && typeof itemData.moveId === 'number') {
        moveData = allMoves.find(m =>
          m.id === itemData.nameEn ||
          m.nameEn === itemData.nameEn ||
          m.name === itemData.name
        );
      }

      if (!moveData) {
        moveData = allMoves.find(m =>
          m.name === itemData.name ||
          m.nameEn === itemData.nameEn
        );
      }

      if (!moveData) {
        console.error('기술을 찾을 수 없습니다:', {
          tmMoveId: itemData.moveId,
          tmName: itemData.name,
          tmNameEn: itemData.nameEn
        });
        alert('기술 정보를 찾을 수 없습니다!');
        return;
      }

      const learnset = getPokemonLearnset(pokemonLearnsets, pokemon);

      if (!learnset) {
        alert(`${pokemon.nickname || pokemon.name}의 기술 습득 정보를 찾을 수 없습니다!`);
        return;
      }

      if (!getLearnsetTmMoves(learnset).includes(moveData.id)) {
        alert(`${pokemon.nickname || pokemon.name}은(는) ${moveData.name}을(를) 배울 수 없습니다!`);
        return;
      }

      const currentMoves = pokemon.moves || [];

      if (currentMoves.some(m => m.moveId === moveData.id)) {
        alert(`${pokemon.nickname || pokemon.name}은(는) 이미 ${moveData.name}을(를) 알고 있습니다!`);
        return;
      }

      if (currentMoves.length < 4) {
        const success = await movesHook.learnMove(pokemon.uniqueId, moveData);
        if (success) {
          consumeItem(item);
        }
        return;
      }

      const moveNames = currentMoves.map((m, idx) => {
        const move = allMoves.find(mv => mv.id === m.moveId);
        return (idx + 1) + '. ' + (move?.name || '???');
      }).join('\n');

      const choice = window.prompt(
        pokemon.nickname || pokemon.name + '이(가) 기술을 4개 알고 있습니다!\n\n현재 기술:\n' + moveNames + '\n\n교체할 기술 번호를 입력하세요(1-4)\n취소하려면 0을 입력하세요'
      );

      if (choice === null || choice === '0') {
        return;
      }

      const choiceNum = parseInt(choice);
      if (isNaN(choiceNum) || choiceNum < 1 || choiceNum > 4) {
        alert('올바른 번호를 입력해주세요');
        return;
      }

      const oldMoveId = currentMoves[choiceNum - 1].moveId;
      const success = await movesHook.learnMove(pokemon.uniqueId, moveData, oldMoveId);

      if (success) {
        consumeItem(item);
      }
      return;
    }

    // 기존 아이템 로직
    const updatedPokemon = { ...pokemon, condition: { ...(pokemon.condition || {}) } };
    let itemUsed = false;
    const effectMessages = [];

    const statNameKo = {
      hp: 'HP', attack: '공격', defense: '방어', specialAttack: '특공', specialDefense: '특방', speed: '스피드',
      elegance: '근사함', beauty: '아름다움', cuteness: '귀여움', intelligence: '슬기로움', strength: '강인함',
    };

    // 특성패치 2단계 - 모달에서 이미 특성을 고른 뒤 재호출된 경우 바로 적용 + 소모
    if (src.specialEffect === 'abilityPatch' && item.__chosenAbility) {
      updatedPokemon.ability = item.__chosenAbility.name;
      updatedPokemon.abilityEn = item.__chosenAbility.nameEn;
      updatedPokemon.isHiddenAbility = Boolean(item.__chosenAbility.isHidden);
      updatePokemonInUser(updatedPokemon);
      alert(`${pokemon.nickname || pokemon.name}의 특성이 ${item.__chosenAbility.name}(으)로 바뀌었습니다!`);
      consumeItem(item);
      return;
    }

    if (src.specialEffect === 'effortEdit' && src.effortOverride) {
      const nextEffort = {
        hp: 0,
        attack: 0,
        defense: 0,
        specialAttack: 0,
        specialDefense: 0,
        speed: 0,
        ...Object.fromEntries(
          Object.entries(src.effortOverride).map(([key, value]) => [
            key,
            Math.min(252, Math.max(0, Number(value) || 0))
          ])
        )
      };
      const totalEffort = Object.values(nextEffort).reduce((sum, value) => sum + Number(value || 0), 0);
      if (totalEffort > 510) {
        alert('기초포인트 총합은 510을 초과할 수 없습니다.');
        return;
      }
      updatedPokemon.effort = nextEffort;
      if (updatedPokemon.effortValues) {
        updatedPokemon.effortValues = nextEffort;
      }
      updatePokemonInUser(updatedPokemon);
      alert(`${pokemon.nickname || pokemon.name}의 기초포인트를 수정했습니다!`);
      consumeItem(item);
      return;
    }

    // isSoyYYNItem은 이름으로 고정 지정된 예전 방식이고, 이제는 관리자가 커스텀 아이템
    // 생성 화면에서 specialEffect를 'effortEdit'으로 직접 선택해서 만들 수도 있다.
    if (isSoyYYNItem(item) || isSoyYYNItem(itemData) || isSoyYYNItem(src) || src.specialEffect === 'effortEdit') {
      if (onRequestStatSelection) {
        releaseItemUseLock();
        onRequestStatSelection(item, pokemon, 'effortEdit', 0);
        return;
      }
    }

    // 사용자가 항목을 선택하는 conditionSelect / evSelect — 실제 값은 모달에서 고른 뒤
    // effortEdit 2단계로 재호출되므로 quantity와 무관하게 항상 1회성 분기다.
    if (src.specialEffect === 'conditionSelect' || src.specialEffect === 'evSelect') {
      if (onRequestStatSelection) {
        releaseItemUseLock();
        onRequestStatSelection(item, pokemon, src.specialEffect, Number(src.boostAmount) || 1);
        return;
      }
    }

    const specialEffect = src.specialEffect;
    const hasBoostConfig = Boolean(
      src.friendshipBoost ||
      (src.ivBoost && !src.isCustom) ||
      src.evBoost ||
      src.conditionBoost ||
      (specialEffect && typeof specialEffect === 'string' && specialEffect.length > 10)
    );

    // friendshipBoost/ivBoost/evBoost/conditionBoost는 N개를 한 번에 써도 똑같은 로직을
    // requestedQuantity번 반복 적용한다. 어느 회차든 스탯이 하나도 안 오르면(전부 캡에
    // 걸림) 그 즉시 멈춰서 나머지를 낭비하지 않는다.
    let boostUses = 0;
    if (hasBoostConfig) {
      for (let use = 0; use < requestedQuantity; use += 1) {
        let changedThisUse = false;

        if (src.friendshipBoost) {
          const baseBoost = src.friendshipBoost;
          const boost = Math.max(0, Math.floor(baseBoost * (pokemon.friendshipGainMultiplier || 1)));
          const current = updatedPokemon.friendship || 0;
          const nextValue = Math.min(255, current + boost);
          if (nextValue > current) {
            updatedPokemon.friendship = nextValue;
            changedThisUse = true;
          }
        }

        if (src.ivBoost && !src.isCustom) {
          const boost = src.ivBoost;
          Object.keys(boost).forEach(stat => {
            if (updatedPokemon.ivs && updatedPokemon.ivs[stat] !== undefined) {
              const current = updatedPokemon.ivs[stat] || 0;
              const nextValue = Math.min(31, current + boost[stat]);
              if (nextValue > current) {
                updatedPokemon.ivs[stat] = nextValue;
                changedThisUse = true;
              }
            }
          });
        }

        if (src.evBoost) {
          const boost = src.evBoost;
          const effort = {
            hp: 0,
            attack: 0,
            defense: 0,
            specialAttack: 0,
            specialDefense: 0,
            speed: 0,
            ...(updatedPokemon.effort || updatedPokemon.effortValues || {})
          };

          Object.keys(boost).forEach(stat => {
            if (effort[stat] !== undefined) {
              const current = Number(effort[stat] || 0);
              const totalEV = Object.values(effort).reduce((sum, v) => sum + Number(v || 0), 0);
              const remaining = 510 - totalEV;
              const actualBoost = Math.min(Number(boost[stat]) || 0, remaining, 252 - current);

              if (actualBoost > 0) {
                effort[stat] = current + actualBoost;
                changedThisUse = true;
              }
            }
          });

          updatedPokemon.effort = effort;
          if (updatedPokemon.effortValues) {
            updatedPokemon.effortValues = effort;
          }
        }

        if (src.conditionBoost) {
          const boost = src.conditionBoost;
          const condMax = Number(systemSettingsRef.current?.conditionMax) > 0
            ? Number(systemSettingsRef.current.conditionMax)
            : 100;
          Object.keys(boost).forEach(condKey => {
            const current = Number(updatedPokemon.condition?.[condKey] || 0);
            if (current >= condMax) return;
            const nextValue = Math.min(condMax, current + boost[condKey]);
            if (nextValue > current) {
              updatedPokemon.condition[condKey] = nextValue;
              changedThisUse = true;
            }
          });
        }

        if (!changedThisUse) break;
        boostUses += 1;
      }

      // 회차별 메시지를 그대로 쌓으면 여러 개 사용 시 줄이 너무 길어지므로, 시작값 대비
      // 최종 순증분만 한 줄씩 계산해서 보여준다.
      if (updatedPokemon.friendship > (pokemon.friendship || 0)) {
        effectMessages.push('친밀도가 ' + updatedPokemon.friendship + '으로 올랐습니다!');
      }
      if (src.ivBoost && !src.isCustom) {
        Object.keys(src.ivBoost).forEach(stat => {
          const before = pokemon.ivs?.[stat] || 0;
          const after = updatedPokemon.ivs?.[stat] || 0;
          if (after > before) effectMessages.push((statNameKo[stat] || stat) + ' 개체값이 ' + after + '으로 올랐습니다!');
        });
      }
      if (src.evBoost) {
        const beforeEffort = pokemon.effort || pokemon.effortValues || {};
        Object.keys(src.evBoost).forEach(stat => {
          const before = Number(beforeEffort[stat] || 0);
          const after = Number(updatedPokemon.effort?.[stat] || 0);
          if (after > before) effectMessages.push((statNameKo[stat] || stat) + ' 기초포인트 ' + (after - before) + '이 상승하였다!');
        });
      }
      if (src.conditionBoost) {
        Object.keys(src.conditionBoost).forEach(condKey => {
          const before = Number(pokemon.condition?.[condKey] || 0);
          const after = Number(updatedPokemon.condition?.[condKey] || 0);
          if (after > before) effectMessages.push((statNameKo[condKey] || condKey) + '이(가) ' + after + '으로 올랐습니다!');
        });
      }
      if (specialEffect && typeof specialEffect === 'string' && specialEffect.length > 10) {
        effectMessages.push('효과: ' + specialEffect);
      }

      itemUsed = true;
    }

    // 진화 아이템(category가 evolution인 경우 + 예외 아이템)은 아래 itemUsed 얼리리턴보다
    // 먼저 체크해야 한다 - 안 그러면 spicy_wailord처럼 친밀도 등 다른 효과가 같이 붙은
    // 진화 아이템은 그 효과 처리에서 먼저 return돼버려 진화 체크에 영영 도달하지 못한다.
    // updatedPokemon을 넘겨서 여기서 오른 친밀도 등이 진화 결과에도 반영되게 한다.
    if (isEvolutionTriggerItem(item, itemData) || EVOLUTION_CHECK_EXCEPTION_ITEMS.has(itemData?.nameEn)) {
      const success = evolutionHook.evolveWithItem(updatedPokemon, itemData.nameEn || itemData.name);
      if (success) {
        consumeItem(item);
        return;
      }
    }

    if (itemUsed) {
      const consumeCount = Math.max(1, boostUses);
      updatePokemonInUser(updatedPokemon);
      const name = pokemon.nickname || pokemon.name;
      const detail = effectMessages.length > 0 ? '\n\n' + effectMessages.join('\n') : '\n\n이미 최대치입니다.';
      const countLabel = consumeCount > 1 ? `을(를) ${consumeCount}개` : '을(를)';
      alert(name + '에게 ' + item.name + countLabel + ' 사용했습니다!' + detail);
      consumeItem(item, {}, consumeCount);
      return;
    }

    // EV 아이템
    if (isEVItem(itemData?.nameEn || itemData?.name)) {
      const evItemName = itemData.nameEn || itemData.name;
      // pokemon(클로저 인자)은 requestedQuantity번 반복해도 갱신되지 않으므로, 회차마다
      // 실제로 반영될 pokemon 스냅샷을 로컬에서 직접 이어받아 다음 회차에 넘긴다.
      let workingPokemon = pokemon;
      let evUses = 0;
      let lastMessage = '';
      for (let use = 0; use < requestedQuantity; use += 1) {
        const result = applyEVItem(workingPokemon, evItemName, (nextPokemon) => { workingPokemon = nextPokemon; });
        if (!result.success) {
          if (evUses === 0) {
            alert(result.message);
            return;
          }
          break;
        }
        evUses += 1;
        lastMessage = result.message;
      }

      await updatePokemonInUser(workingPokemon);
      await consumeItem(item, {}, evUses);
      alert(evUses > 1 ? `${lastMessage}\n\n(총 ${evUses}개 사용)` : lastMessage);
      return;
    }

    // 이상한사탕
    if (isRareCandyItem(item, itemData)) {
      let candyUses = 0;
      for (let use = 0; use < requestedQuantity; use += 1) {
        const success = await handleRareCandyWithEvolution(pokemon.uniqueId);
        if (!success) break;
        candyUses += 1;
      }
      if (candyUses > 0) {
        await consumeItem(item, {}, candyUses);
      }
      return;
    }

    // 폼체인지 아이템 (꿀 포함)
    const nectarFormNameEn = NECTAR_FORM_MAP[itemData?.nameEn];
    const formChangeNumbers = FORM_CHANGE_ITEMS[itemData?.nameEn];
    const isNectar = Boolean(nectarFormNameEn);
    const isFormChangeItem = isNectar || Boolean(formChangeNumbers);

    if (isFormChangeItem && typeof changePokemonForm === 'function') {
      // UI에서 이미 폼이 선택된 경우 (selectedFormNameEn 전달됨)
      const targetFormNameEn = selectedFormNameEn || nectarFormNameEn;
      if (targetFormNameEn) {
        const success = await changePokemonForm(pokemon.uniqueId, targetFormNameEn);
        if (success) {
          if (isNectar || CONSUMABLE_FORM_CHANGE_ITEMS.has(itemData?.nameEn)) consumeItem(item);
        }
        return;
      }
      // fallback: 선택 UI 없이 호출된 경우 (기존 prompt 방식)
      if (formChangeNumbers && typeof getPokemonFormCandidates === 'function') {
        const baseNumber = Number(pokemon.originalNumber || pokemon.number);
        if (!formChangeNumbers.includes(baseNumber)) {
          alert('이 아이템은 이 포켓몬에게 사용할 수 없습니다!');
          releaseItemUseLock();
          return;
        }
        const forms = getPokemonFormCandidates(pokemon);
        const otherForms = forms.filter(f => f.nameEn !== pokemon.nameEn);
        if (otherForms.length === 0) {
          alert('변경 가능한 폼이 없습니다.');
          releaseItemUseLock();
          return;
        }
        const formList = otherForms.map((f, i) => `${i + 1}. ${f.name || f.nameEn}`).join('\n');
        const choice = window.prompt(`${pokemon.nickname || pokemon.name}의 폼을 선택하세요:\n\n${formList}\n\n번호를 입력하세요 (취소: 0)`);
        if (!choice || choice === '0') { releaseItemUseLock(); return; }
        const idx = parseInt(choice) - 1;
        if (isNaN(idx) || idx < 0 || idx >= otherForms.length) {
          alert('올바른 번호를 입력해주세요.');
          releaseItemUseLock();
          return;
        }
        await changePokemonForm(pokemon.uniqueId, otherForms[idx].id || otherForms[idx].nameEn || otherForms[idx].name);
      }
      return;
    }

    alert((pokemon.nickname || pokemon.name) + '에게 ' + item.name + '을(를) 사용했습니다!');
    consumeItem(item);
    } finally {
      releaseItemUseLock();
    }
  };

  return {
    useItemOnPokemon
  };
};

export default useItemEffects;
